import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  SearchState,
  SearchTrackingTypes,
  updateOriginallySearchedText,
  updateSearchTrackingType,
} from '@commerce-search-webcomponents/sdk';
import { Breakpoints, FAIcon, MLPVersion, Search, useBreakpoint, useTranslation } from '@nextgen-web-framework/all';
import { SearchBarPropTypes } from './types/propTypes';
import styles from './SearchBar.module.scss';

const SearchBar = ({ searchText, setSearchText, onSearch, openOverlay, expanded }: SearchBarPropTypes) => {
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
  const [showMiniSearch, setShowMiniSearch] = useState<boolean>(true);
  const alternateTermRef = useRef<string | undefined>();
  const { typeaheadSearchResults } = useSelector((state: { commerceSearch: SearchState }) => state.commerceSearch);
  const dispatch = useDispatch();
  const viewPortType = useBreakpoint() as Breakpoints;
  const t = useTranslation();
  const searchTextMinLength = 2;
  const animationRuntime = 301;

  useEffect(() => {
    const searchInput = document.querySelector('.search-box-wrapper input') as HTMLInputElement;
    searchInput?.addEventListener('focus', () => {
      openOverlay();
    });
  }, [viewPortType]);

  useLayoutEffect(() => {
    handleExpandedPropChange();
  }, [expanded]);

  useEffect(() => {
    if (!showMiniSearch) {
      const searchInput = document.querySelector('.search-box-wrapper input') as HTMLInputElement;
      searchInput?.focus();
    }
  }, [showMiniSearch]);

  useEffect(() => {
    alternateTermRef.current = '';
  }, [searchText]);

  useEffect(() => {
    const text = typeaheadSearchResults.alternateTerms?.[0];
    if (text) {
      alternateTermRef.current = text;
    }
  }, [typeaheadSearchResults]);

  const handleExpandedPropChange = () => {
    if (expanded) {
      setSearchExpanded(true);
      setShowMiniSearch(false);
      return;
    }
    setTimeout(() => {
      setShowMiniSearch(true);
    }, animationRuntime);
  };

  const handleOnSearch = () => {
    let originallySearchedText = '';
    let query = searchText;
    let searchType = SearchTrackingTypes.ENTERED;
    if (alternateTermRef.current) {
      originallySearchedText = searchText;
      query = alternateTermRef.current;
      searchType = SearchTrackingTypes.CORRECTED;
    }
    if (searchText.length >= searchTextMinLength) {
      dispatch(updateOriginallySearchedText(originallySearchedText));
      dispatch(updateSearchTrackingType(searchType));
      onSearch?.(undefined, query);
      const searchInput = document.querySelector('.search-box-wrapper input') as HTMLInputElement;
      searchInput?.blur();
    }
  };

  const collapsedClassName = searchExpanded ? styles.collapsed : '';
  const conditionalClassName = expanded ? styles.expanded : collapsedClassName;

  return viewPortType === Breakpoints.XL || !showMiniSearch ? (
    <Search
      mlpVersion={MLPVersion.TWO}
      className={`${styles.searchBox} ${conditionalClassName} search-box-wrapper`}
      value={searchText}
      placeholder={t('search.searchBar.placeholder')}
      onSearch={handleOnSearch}
      onChange={(e) => setSearchText(e?.target?.value)}
      onClear={() => setSearchText('')}
    />
  ) : (
    <span className={styles['mini-search-icon-container']}>
      <FAIcon iconName="magnifying-glass" className={styles['mini-search-icon']} onClick={openOverlay} />
    </span>
  );
};

export default SearchBar;
export type { SearchBarPropTypes };
